import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Bell, CheckCheck, Trash2 } from 'lucide-react'
import { useNotificationStore } from '@/store/notification.store'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/utils/cn'
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery'
import { NotificationListItem } from '@/modules/notifications/components/NotificationListItem'
import {
  resolveNotificationEntityHref,
  type NotificationItem,
} from '@/modules/notifications/services/notification.service'

interface NotificationBellProps {
  /** `header` = desktop app header. `mobile` = mobile app bar. Both are
   * neutral surfaces since Phase 4.5 — styled identically in spirit, just
   * sized differently for their context. */
  variant?: 'header' | 'mobile'
}

/** Opaque panel — never glass-overlay (content behind must not bleed through). */
const PANEL_SURFACE =
  'overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-elevated'

/** Row exit: slide/collapse so the rows below move up smoothly. */
const ROW_EXIT = { opacity: 0, height: 0, transition: { duration: 0.18, ease: 'easeOut' as const } }

function NotificationPanel({
  onClose,
  onClearAll,
}: {
  onClose: () => void
  onClearAll: () => void
}) {
  const navigate = useNavigate()
  const reducedMotion = usePrefersReducedMotion()
  const items = useNotificationStore((s) => s.items)
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const isLoading = useNotificationStore((s) => s.isLoading)
  const markRead = useNotificationStore((s) => s.markRead)
  const markAllRead = useNotificationStore((s) => s.markAllRead)
  const toggleRead = useNotificationStore((s) => s.toggleRead)
  const removeNotification = useNotificationStore((s) => s.remove)

  const handleSelect = (notification: NotificationItem) => {
    if (!notification.readAt) void markRead(notification.id)
    const href = resolveNotificationEntityHref(notification)
    onClose()
    if (href) navigate(href)
  }

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b border-border/60 bg-card px-3 py-2">
        <p className="shrink-0 text-sm font-semibold text-foreground">Notifications</p>
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 gap-1 px-2 text-xs text-muted-foreground disabled:opacity-40"
            disabled={unreadCount === 0}
            onClick={(event) => {
              event.preventDefault()
              void markAllRead()
            }}
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Mark all read
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive disabled:opacity-40"
            disabled={items.length === 0}
            aria-label="Clear all"
            title="Clear all"
            onClick={(event) => {
              event.preventDefault()
              onClearAll()
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="max-h-[min(70vh,24rem)] overflow-x-hidden overflow-y-auto overscroll-contain bg-card">
        {isLoading && items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">You're all caught up</p>
        ) : (
          <ul className="w-full min-w-0 divide-y divide-border/40">
            <AnimatePresence initial={false}>
              {items.map((notification) => (
                <motion.li
                  key={notification.id}
                  layout={!reducedMotion}
                  exit={reducedMotion ? undefined : ROW_EXIT}
                  className="min-w-0 overflow-hidden"
                >
                  <NotificationListItem
                    notification={notification}
                    variant="panel"
                    onOpen={handleSelect}
                    onToggleRead={(id) => void toggleRead(id)}
                    onDelete={removeNotification}
                  />
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          onClose()
          navigate(ROUTES.NOTIFICATIONS)
        }}
        className="w-full border-t border-border/60 bg-card px-3 py-2.5 text-center text-xs font-medium text-primary hover:bg-muted/50"
      >
        View all notifications
      </button>
    </>
  )
}

function MobileNotificationOverlay({
  open,
  onOpenChange,
  onClearAll,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onClearAll: () => void
}) {
  const reducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onOpenChange])

  if (typeof document === 'undefined') return null

  const overlayTween = reducedMotion
    ? { duration: 0.01 }
    : { duration: 0.12, ease: 'easeOut' as const }
  const panelTween = reducedMotion
    ? { duration: 0.01 }
    : { duration: 0.14, ease: [0.22, 1, 0.36, 1] as const }

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          key="mobile-notifications"
          className="fixed inset-0 z-[80]"
          role="presentation"
          initial={false}
        >
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={overlayTween}
            className="absolute inset-0 bg-black/45"
            aria-label="Close notifications"
            onClick={() => onOpenChange(false)}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Notifications"
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.98 }}
            transition={panelTween}
            className={cn(
              PANEL_SURFACE,
              'absolute left-1/2 z-10 w-[min(22rem,calc(100vw-1.5rem))] -translate-x-1/2',
              'top-[calc(3.5rem+env(safe-area-inset-top,0px)+0.5rem)]',
            )}
          >
            <NotificationPanel onClose={() => onOpenChange(false)} onClearAll={onClearAll} />
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}

/** Notification preview dropdown, shared by the desktop header and the mobile app bar. */
export function NotificationBell({ variant = 'header' }: NotificationBellProps) {
  const isHeader = variant === 'header'
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const hasLoadedOnce = useNotificationStore((s) => s.hasLoadedOnce)
  const fetchInbox = useNotificationStore((s) => s.fetchInbox)
  const clearAll = useNotificationStore((s) => s.clearAll)
  const [open, setOpen] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)

  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next && !hasLoadedOnce) void fetchInbox()
  }

  // Close the panel first — the confirm must not sit under / inside it.
  const askClearAll = () => {
    setOpen(false)
    setConfirmClear(true)
  }

  const clearConfirm = (
    <ConfirmDialog
      open={confirmClear}
      onOpenChange={setConfirmClear}
      title="Clear all notifications?"
      description="This can't be undone."
      confirmLabel="Clear all"
      variant="destructive"
      onConfirm={() => {
        setConfirmClear(false)
        void clearAll()
      }}
    />
  )

  const trigger = (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        'relative shrink-0',
        // Bell carries potentially-urgent unread state, so it stays
        // full-contrast text-foreground at rest (not the muted-foreground
        // other header toggles use) — it must never blend into the bar.
        isHeader
          ? 'h-9 w-9 rounded-lg text-foreground hover:bg-secondary sm:h-10 sm:w-10'
          : 'h-9 w-9 rounded-xl bg-muted/50 text-muted-foreground hover:bg-muted',
      )}
      aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
      aria-expanded={open}
      onClick={!isHeader ? () => handleOpenChange(!open) : undefined}
    >
      <Bell className="h-[18px] w-[18px]" strokeWidth={2} />
      {unreadCount > 0 ? (
        <span
          className={cn(
            'absolute -right-1.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1',
            'text-[8px] font-bold leading-none tabular-nums text-white ring-2',
            isHeader ? 'ring-card' : 'ring-background',
          )}
        >
          {badgeLabel}
        </span>
      ) : null}
    </Button>
  )

  // Mobile app bar: portal to body. Fixed positioning inside glass-header
  // (backdrop-filter) is trapped to the header and the panel paints over the
  // page without a reliable opaque surface.
  if (!isHeader) {
    return (
      <>
        {trigger}
        <MobileNotificationOverlay open={open} onOpenChange={handleOpenChange} onClearAll={askClearAll} />
        {clearConfirm}
      </>
    )
  }

  return (
    <>
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="bottom"
        sideOffset={8}
        collisionPadding={8}
        className={cn(
          PANEL_SURFACE,
          // Defeat shared glass-overlay translucency from DropdownMenuContent.
          'z-[80] w-[22rem] max-w-[min(22rem,calc(100vw-1.5rem))] p-0',
          '![background:var(--color-card)] ![backdrop-filter:none] ![-webkit-backdrop-filter:none]',
        )}
      >
        <NotificationPanel onClose={() => handleOpenChange(false)} onClearAll={askClearAll} />
      </DropdownMenuContent>
    </DropdownMenu>
    {clearConfirm}
    </>
  )
}
